import {
  readSessionItem,
  replaceLegacyMarketingUtmsInUrl,
  resolveUtmCampaign,
  resolveUtmSourceAndMedium,
  sanitizeMarketingUtmMedium,
  sanitizeMarketingUtmSource,
  urlSinUtmsPrincipales,
  UTM_DEFAULT_CAMPAIGN,
  UTM_DEFAULT_MEDIUM,
  UTM_DEFAULT_SOURCE,
} from "../utils/utmParams";

export {
  UTM_DEFAULT_SOURCE,
  UTM_DEFAULT_MEDIUM,
  UTM_DEFAULT_CAMPAIGN,
  urlSinUtmsPrincipales,
  sanitizeMarketingUtmSource,
  sanitizeMarketingUtmMedium,
} from "../utils/utmParams";

function writeSessionItem(key, value) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

function removeSessionItem(key) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

function normalizeOptional(value) {
  if (value == null) return null;
  const t = String(value).trim();
  return t === "" ? null : t;
}

/**
 * Persiste UTM en sessionStorage al cargar (llamar desde index.js).
 * Sin UTMs principales en URL → defaults de marca (incl. `utm_campaign=jeep`) y limpia `utm_term`.
 * Con UTMs en URL → persiste cada param presente en query y alinea source/medium efectivos.
 */
export function saveUtmParams() {
  if (typeof window === "undefined") return;

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    params = new URLSearchParams();
  }

  if (urlSinUtmsPrincipales(window.location.search)) {
    writeSessionItem("utm_source", UTM_DEFAULT_SOURCE);
    writeSessionItem("utm_medium", UTM_DEFAULT_MEDIUM);
    writeSessionItem("utm_campaign", UTM_DEFAULT_CAMPAIGN);
    removeSessionItem("utm_term");
    return;
  }

  const campaign = normalizeOptional(params.get("utm_campaign"));
  const term = normalizeOptional(params.get("utm_term"));
  if (campaign) writeSessionItem("utm_campaign", campaign);
  if (term) writeSessionItem("utm_term", term);

  const src = sanitizeMarketingUtmSource(params.get("utm_source"));
  const med = sanitizeMarketingUtmMedium(params.get("utm_medium"));
  if (src) writeSessionItem("utm_source", src);
  if (med) writeSessionItem("utm_medium", med);

  const { utm_source, utm_medium } = resolveUtmSourceAndMedium();
  writeSessionItem("utm_source", utm_source);
  writeSessionItem("utm_medium", utm_medium);
}

/**
 * UTM coherentes para leads / WhatsApp / Tecnóm.
 * Sin `window`, devuelve nulls como guardarraíl (SSR o tests).
 */
export function getUtmParams() {
  if (typeof window === "undefined") {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
      utm_campaign: UTM_DEFAULT_CAMPAIGN,
      utm_term: null,
    };
  }

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    params = new URLSearchParams();
  }

  if (urlSinUtmsPrincipales(window.location.search)) {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
      utm_campaign: UTM_DEFAULT_CAMPAIGN,
      utm_term: null,
    };
  }

  const { utm_source, utm_medium } = resolveUtmSourceAndMedium();
  const utm_campaign = resolveUtmCampaign(window.location.search);

  const urlTerm = normalizeOptional(params.get("utm_term"));
  const utm_term = urlTerm || readSessionItem("utm_term");

  return { utm_source, utm_medium, utm_campaign, utm_term };
}

/** `?utm_source=...&utm_medium=...&utm_campaign=...` usando `getUtmParams()` y defaults si hiciera falta. */
export function buildUtmQueryString() {
  if (typeof window === "undefined") {
    return `?utm_source=${encodeURIComponent(UTM_DEFAULT_SOURCE)}&utm_medium=${encodeURIComponent(
      UTM_DEFAULT_MEDIUM
    )}&utm_campaign=${encodeURIComponent(UTM_DEFAULT_CAMPAIGN)}`;
  }
  const { utm_source, utm_medium, utm_campaign } = getUtmParams();
  const s = utm_source || UTM_DEFAULT_SOURCE;
  const m = utm_medium || UTM_DEFAULT_MEDIUM;
  const c = utm_campaign || UTM_DEFAULT_CAMPAIGN;
  return `?utm_source=${encodeURIComponent(s)}&utm_medium=${encodeURIComponent(m)}&utm_campaign=${encodeURIComponent(c)}`;
}

/** Conserva query de campaña al navegar por rutas internas. */
export function getNavigationSearch(searchFromRouter = "") {
  if (searchFromRouter && searchFromRouter.startsWith("?")) {
    return searchFromRouter;
  }
  if (typeof window !== "undefined" && window.location.search) {
    return window.location.search;
  }
  return buildUtmQueryString();
}

export function buildRouteWithUtms(pathname, searchFromRouter = "") {
  return {
    pathname,
    search: getNavigationSearch(searchFromRouter),
  };
}

/**
 * URL actual con UTMs alineados a la guía de negocio (defaults si no hay principales en query;
 * no fuerza `utm_source=CONSULTA WEB`; completa lo que falte desde sesión/defaults).
 */
export function getCurrentUrlWithUtmDefaults() {
  if (typeof window === "undefined") return "";
  const u = new URL(window.location.href);
  const resolved = getUtmParams();

  if (urlSinUtmsPrincipales(window.location.search)) {
    u.searchParams.set("utm_source", resolved.utm_source || UTM_DEFAULT_SOURCE);
    u.searchParams.set("utm_medium", resolved.utm_medium || UTM_DEFAULT_MEDIUM);
    u.searchParams.set("utm_campaign", resolved.utm_campaign || UTM_DEFAULT_CAMPAIGN);
    u.searchParams.delete("utm_term");
    replaceLegacyMarketingUtmsInUrl(u, resolved.utm_source, resolved.utm_medium);
    return u.toString();
  }

  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term"];
  for (const key of keys) {
    const inQuery = normalizeOptional(u.searchParams.get(key));
    const val = resolved[key];
    if (!inQuery && val) {
      u.searchParams.set(key, val);
    }
  }
  replaceLegacyMarketingUtmsInUrl(u, resolved.utm_source, resolved.utm_medium);
  return u.toString();
}

/** Teléfono WhatsApp para consultas (sin + ni espacios). */
export const WHATSAPP_CONSULTA_PHONE = "5493513743833";

const WHATSAPP_CONSULTA_MESSAGE = "Hola, me gustaría hacer una consulta.";

/** Abre WhatsApp con mensaje y URL actual con UTMs alineados. */
export function openWhatsAppConsulta() {
  if (typeof window === "undefined") return;
  const urlWithUtm = getCurrentUrlWithUtmDefaults();
  const whatsappMessage = `${WHATSAPP_CONSULTA_MESSAGE}\n${urlWithUtm}`;
  const encodedMessage = encodeURIComponent(whatsappMessage);
  window.location.href = `https://api.whatsapp.com/send?phone=${WHATSAPP_CONSULTA_PHONE}&text=${encodedMessage}`;
}
